import { Clock3, Construction, ShieldCheck } from 'lucide-react';
import { roleMeta, roleNavigation } from '../navigation';
import type { Role } from '../../shared/types/domain';
import { Card, EmptyState, StatCard } from '../../shared/ui/Card';
import { StatusBadge } from '../../shared/ui/Status';
import { useAuth } from '../../features/auth/AuthProvider';

const homePaths: Record<Role, string> = { member: 'home', employee: 'workbench', manager: 'dashboard', super: 'dashboard' };

export function PortalPage({ role, page }: { role: Role; page: string }) {
  const { session } = useAuth();
  const item = roleNavigation[role].find((candidate) => candidate.path === page) ?? roleNavigation[role][0];
  const isHome = page === homePaths[role];
  return (
    <div className="page-stack">
      <header className="page-heading">
        <div><span>{roleMeta[role].eyebrow}</span><h1>{item.label}</h1><p>{item.description}</p></div>
        <StatusBadge tone="success">底座已就绪</StatusBadge>
      </header>
      {isHome && <FoundationOverview role={role} storeName={session?.profile.storeName} />}
      {!isHome && (
        <EmptyState
          title={`${item.label}业务模块将在对应迭代接入`}
          description="当前页面已完成路由、会话、角色隔离、响应式布局与统一状态组件。后续业务不会再堆叠进单一入口文件。"
          action={<StatusBadge>非占位假数据 · 仅工程空壳</StatusBadge>}
        />
      )}
    </div>
  );
}

function FoundationOverview({ role, storeName }: { role: Role; storeName?: string | null }) {
  const summaries: Record<Role, [string, string, string][]> = {
    member: [['今日课程', '—', '排课模块接入后展示'], ['剩余课时', '—', '课程包数据接入后展示'], ['待评分', '—', '反馈闭环接入后展示']],
    employee: [['今日课表', '—', '排课模块接入后展示'], ['待反馈', '—', '课消模块接入后展示'], ['会员风险', '—', '会员模块接入后展示']],
    manager: [['今日到课', '—', '门店聚合查询接入后展示'], ['待审批', '—', '审批模块接入后展示'], ['低评分', '—', '评分模块接入后展示']],
    super: [['门店数', '4', 'V4确认的直营门店'], ['风险事项', '—', '运营模块接入后展示'], ['本月目标', '—', '目标模块接入后展示']],
  };
  return <>
    <div className="stat-grid">{summaries[role].map(([label, value, detail]) => <StatCard key={label} label={label} value={value} detail={detail} />)}</div>
    <div className="foundation-grid">
      <Card><ShieldCheck /><div><h2>权限在服务端再次校验</h2><p>前端权限只改善体验，不作为安全边界。所有关键动作预留RPC与RLS双重校验。</p></div></Card>
      <Card><Clock3 /><div><h2>{storeName ?? '全公司'}数据范围</h2><p>当前会话已经绑定角色与门店范围，后续查询统一通过范围上下文执行。</p></div></Card>
      <Card><Construction /><div><h2>按闭环逐步接入</h2><p>工程底座完成后依次建设账号组织、购课、履约、教学、激励与商城闭环。</p></div></Card>
    </div>
  </>;
}
